import { Component, Output, EventEmitter, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

type TensionLevel = 'fluide' | 'tension' | 'critique';
type Dispo = 'disponible' | 'occupé' | 'en-route';
type MyStatus = 'disponible' | 'occupé' | 'en-route';
type TlColor = 'vert' | 'orange' | 'rouge';

interface Collegue {
  id: number;
  nom: string;
  role: string;
  dispo: Dispo;
  site: string;
}

@Component({
  selector: 'app-mon-site-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './mon-site-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mon-site-screen.component.scss'],
})
export class MonSiteScreenComponent {
  @Output() openRenfort = new EventEmitter<void>();
  @Output() openModeDegrade = new EventEmitter<void>();
  @Output() openCommPatient = new EventEmitter<void>();
  @Output() openCanal = new EventEmitter<void>();
  @Output() openCanalWith = new EventEmitter<string>();
  @Output() openNotifAide = new EventEmitter<void>();

  // ── Site ──
  readonly siteName = 'Nantes Centre';
  readonly tension = signal<TensionLevel>('critique');
  readonly timeline: TlColor[] = ['vert', 'vert', 'orange', 'orange', 'rouge', 'rouge'];
  readonly patients = signal(28);
  readonly enAttente = signal(14);
  readonly attente = signal(18);
  readonly chargeCapacite = signal(130);
  readonly sallesOccupees = signal(4);
  readonly sallesTotales = signal(5);
  readonly deriveMinutes = signal(45);
  readonly actionRecommandee = signal('Alerte envoyée aux sites disponibles');
  readonly actionSent = signal<string | null>(null);

  getTimelineSlots(): string[] {
    const now = new Date();
    const slots: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 15 * 60000);
      const h = String(t.getHours()).padStart(2, '0');
      const m = String(Math.floor(t.getMinutes() / 15) * 15).padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
    return slots;
  }

  tlColor(c: TlColor): string {
    return ({ vert: '#10B981', orange: '#F59E0B', rouge: '#EF4444' })[c];
  }

  tensionLabel(): string {
    const map: Record<TensionLevel, string> = { fluide: 'Fluide', tension: 'En tension', critique: 'Critique' };
    return map[this.tension()];
  }

  sendAction(action: string) {
    this.actionSent.set(action);
    setTimeout(() => this.actionSent.set(null), 2500);
  }

  sallesPct(): number { return Math.round(this.sallesOccupees() / this.sallesTotales() * 100); }

  readonly actions = [
    { id: 'renfort',   label: 'Demander renfort', icon: '🆘' },
    { id: 'canal',     label: 'Démarrer une conversation',   icon: '💬' },
    { id: 'degrade',   label: 'Mode dégradé',      icon: '⚠️' },
    { id: 'patient',   label: 'Comm. patient',     icon: '📢' },
  ];

  // ── Équipe ──
  readonly myStatus = signal<MyStatus | null>('disponible');
  readonly activeActionLabel = signal<string | null>(null);
  readonly activeActionId = signal<string | null>(null);

  readonly collegues = signal<Collegue[]>([
    { id: 1,  nom: 'Marie L.',    role: 'Préleveur',   dispo: 'disponible', site: 'Rezé'           },
    { id: 2,  nom: 'Paul R.',     role: 'Préleveur',   dispo: 'en-route',   site: 'Nantes Centre'  },
    { id: 3,  nom: 'Claire B.',   role: 'Secrétaire',  dispo: 'occupé',     site: 'Saint-Herblain' },
    { id: 4,  nom: 'Thomas M.',   role: 'Biologiste',  dispo: 'disponible', site: 'Orvault'        },
    { id: 5,  nom: 'Sophie D.',   role: 'Secrétaire',  dispo: 'disponible', site: 'Vertou'         },
    { id: 6,  nom: 'Lucas F.',    role: 'Préleveur',   dispo: 'occupé',     site: 'Nantes Centre'  },
    { id: 7,  nom: 'Nathalie C.', role: 'Secrétaire',  dispo: 'disponible', site: 'Carquefou'      },
    { id: 8,  nom: 'Julien P.',   role: 'Technicien',  dispo: 'en-route',   site: 'Nantes Centre'  },
    { id: 9,  nom: 'Émilie R.',   role: 'Préleveur',   dispo: 'disponible', site: 'Rezé'           },
    { id: 10, nom: 'Antoine V.',  role: 'Biologiste',  dispo: 'occupé',     site: 'Saint-Herblain' },
  ]);

  readonly myActions: { id: string; label: string; icon: string; dispo: MyStatus }[] = [
    { id: 'aide',      label: 'Je peux aider',          icon: '🙋', dispo: 'disponible' },
    { id: 'appels',    label: 'Je prends les appels',    icon: '📞', dispo: 'occupé'     },
    { id: 'route',     label: 'Je pars sur site',        icon: '🚗', dispo: 'en-route'   },
    { id: 'resultats', label: 'Je prends les résultats', icon: '📋', dispo: 'occupé'     },
  ];

  dispoLabel(d: Dispo): string {
    const map: Record<Dispo, string> = { disponible: 'Disponible', 'occupé': 'Occupé', 'en-route': 'En route' };
    return map[d];
  }

  dispoColor(d: Dispo): string {
    const map: Record<Dispo, string> = { disponible: '#16a34a', 'occupé': '#dc2626', 'en-route': '#d97706' };
    return map[d];
  }

  sendMessage(nom: string) {
    this.openCanalWith.emit(nom);
  }

  sendTeamAction(label: string, dispo: MyStatus, id: string) {
    if (this.activeActionId() === id) {
      this.myStatus.set('disponible');
      this.activeActionLabel.set(null);
      this.activeActionId.set(null);
    } else {
      this.myStatus.set(dispo);
      this.activeActionLabel.set(label);
      this.activeActionId.set(id);
    }
  }

  myStatusLabel(): string {
    const s = this.myStatus();
    if (!s) return '';
    return ({ disponible: 'Disponible', 'occupé': 'Occupé', 'en-route': 'En route' })[s];
  }

  toggleStatus() {
    this.myStatus.set(this.myStatus() === 'disponible' ? 'occupé' : 'disponible');
    this.activeActionLabel.set(null);
    this.activeActionId.set(null);
  }
}